/**
 * The lab's "other player".
 *
 * Every button here goes through `applyMessage`, i.e. through the REAL
 * reducer, with a real schema-shaped packet — so playing both sides in the lab
 * exercises exactly the code path a socket message would. Nothing in this file
 * is imported by the screen.
 */
import {Chess} from 'chess.js';

import {liveGameStore} from '@/Store/liveGameStore';
import {LAB_OPPONENT_DISCONNECTED} from '@/Store/liveGame/selectors';
import {id} from '@/Store/liveGame/state';

/** whose clock has been burning, and for how long */
const burn = (state: any, now: number) => {
  const elapsed = state.game.clockRunningSince
    ? now - state.game.clockRunningSince
    : 0;
  const moverIsWhite = id(state.game.turnUserId) === id(state.game.whiteId);
  const whiteMs = Math.max(
    0,
    state.game.whiteTimeMs - (moverIsWhite ? elapsed : 0),
  );
  const blackMs = Math.max(
    0,
    state.game.blackTimeMs - (moverIsWhite ? 0 : elapsed),
  );
  return {
    // the wire carries whole SECONDS (protocol contract)
    whiteTime: Math.round(
      (whiteMs + (moverIsWhite ? state.game.incMs : 0)) / 1000,
    ),
    blackTime: Math.round(
      (blackMs + (moverIsWhite ? 0 : state.game.incMs)) / 1000,
    ),
  };
};

/** Play a random legal move for whoever is to move in `game.fen`. */
export function playRandomMove(now: number = Date.now()): string | null {
  const state = liveGameStore.getState();
  let chess: Chess;
  try {
    chess = new Chess(state.game.fen);
  } catch (e) {
    return null;
  }
  const legal = chess.moves();
  if (legal.length === 0) {
    return null;
  }
  const san = legal[Math.floor(Math.random() * legal.length)];
  const previousFen = chess.fen();
  chess.move(san);

  const moverId = id(state.game.turnUserId);
  const nextUserId =
    moverId === id(state.game.whiteId)
      ? state.game.blackId
      : state.game.whiteId;

  liveGameStore.getState().applyMessage('player_turn_response', {
    requestId: 'player move',
    type: 'player_turn_response',
    userId: nextUserId,
    tableId: state.game.tableId,
    data: chess.fen(),
    ...burn(state, now),
    moves: [san],
    whitePiecesCaptured: [],
    blackPiecesCaptured: [],
    previousFen,
  });
  return san;
}

/** draw_check — `userId` is the party being OFFERED the draw, i.e. me. */
export function offerDrawAsOpponent(): void {
  const state = liveGameStore.getState();
  liveGameStore.getState().applyMessage('draw_check', {
    requestId: 'draw',
    type: 'draw_check',
    userId: state.session.me,
    tableId: state.game.tableId,
    requester: state.game.opponentId,
  });
}

/** draw_reject — the opponent turned MY offer down. */
export function rejectDrawAsOpponent(): void {
  const state = liveGameStore.getState();
  liveGameStore.getState().applyMessage('draw_reject', {
    requestId: 'draw',
    type: 'draw_reject',
    userId: state.session.me,
    tableId: state.game.tableId,
    responsee: state.game.opponentId,
  });
}

/** game_end_response — checkmate, me winning. */
export function endGameAsCheckmate(): void {
  const state = liveGameStore.getState();
  liveGameStore.getState().applyMessage('game_end_response', {
    requestId: 'game end',
    type: 'game_end_response',
    endType: 'checkmate',
    userId: state.game.opponentId,
    tableId: state.game.tableId,
    gameState: state.game.fen,
    winner: state.session.me,
    pgn: '',
    moves: state.game.moves,
    whitePiecesCaptured: [],
    blackPiecesCaptured: [],
    opponent: state.game.opponentId,
    playerMoveTimer: state.game.tcMs,
    moveTimeIncrement: state.game.incMs,
    white: state.game.whiteId,
    winner_rating: 1494,
    opponent_rating: 1498,
    winner_rating_delta: '12.4',
    opponent_rating_delta: '-12.4',
    winner_gameId: 'lab-game-uuid-mine',
    opponent_gameId: 'lab-game-uuid-theirs',
  });
}

/**
 * The M1a stand-in for the opponent-disconnect signal (there is no such
 * message on the wire yet — see the TODO in LiveBody). It only stamps
 * `meta.lastMessageType`, which is what `selectLiveEvents` reads.
 */
export function toggleOpponentDisconnected(): void {
  liveGameStore.setState(s => ({
    ...s,
    meta: {
      ...s.meta,
      lastMessageType:
        s.meta.lastMessageType === LAB_OPPONENT_DISCONNECTED
          ? null
          : LAB_OPPONENT_DISCONNECTED,
    },
  }));
}
